/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, May 11th 2022, 2:52:50 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useGlobalScope } from "core/providers";
import { getCurrentURL, getAbsoluteURL, resolveURLName } from "core/routes/utils";
import { SubscriptionPlan, SubscriptionTracker } from "saas-subscription/schemas";
import { Organization } from "lib/schemas";
import { useAPIRequest, APISERVICEPATHS } from "lib/api";

const subscriptionAPIBaseURL = process.env.REACT_APP_ONESTOP_SUBSCRIPTION_API_BASE_URL;

const useSubscriptionPlansList = () => {
  const [subscriptionPlans, setSubscriptionPlans] = React.useState<SubscriptionPlan[]>([]);

  const { makeRequest, makeRequestCleanUp, ...apiRequestState } = useAPIRequest<SubscriptionPlan[]>({
    apiBaseURL: subscriptionAPIBaseURL,
    apiServicePath: APISERVICEPATHS.subscriptionPlansList,
    makeRequestOnInitialMount: false,
  });

  React.useEffect(() => {
    makeRequest({
      setValidAPIResponse: (data) => setSubscriptionPlans(data),
      credentials: "omit",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { subscriptionPlans, ...apiRequestState };
};

const useCreateSetupCheckoutSession = (organization: Organization) => {
  const [checkoutURL, setCheckoutURL] = React.useState<string>();

  const { makeRequest, makeRequestCleanUp, ...checkoutRequestState } = useAPIRequest<any>({
    apiBaseURL: subscriptionAPIBaseURL,
    apiServicePath: APISERVICEPATHS.setupCreateCheckoutSession,
    makeRequestOnInitialMount: false,
  });

  const createSetupCheckout = React.useCallback(() => {
    if (!organization) return;

    const body = JSON.stringify({
      organization_id: organization.id,
      success_url: getAbsoluteURL(resolveURLName("subscriptionSuccess", { organizationId: organization.id })),
      cancel_url: getCurrentURL(),
    });

    makeRequest({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      setValidAPIResponse: (data) => setCheckoutURL(data.checkout_session_url),
      credentials: "omit",
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  React.useEffect(() => {
    checkoutURL && window.location.assign(checkoutURL);
  }, [checkoutURL]);

  return { createSetupCheckout, ...checkoutRequestState };
};

type ILookupKey = SubscriptionPlan["prices"][number]["lookup_key"];

const useCreateCheckoutSession = () => {
  const [checkoutURL, setCheckoutURL] = React.useState<string>();
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const { makeRequest, makeRequestCleanUp, ...checkoutRequestState } = useAPIRequest<any>({
    apiBaseURL: subscriptionAPIBaseURL,
    apiServicePath: APISERVICEPATHS.subscriptionCreateCheckoutSession,
    makeRequestOnInitialMount: false,
  });

  const createCheckoutSession = (organization: Organization, lookup_key: ILookupKey) => {
    if (!lookup_key || !organization) return;

    const body = JSON.stringify({
      lookup_key,
      organization_id: organization.id,
      organization_email: organization.email,
      organization_name: organization.name,
      organization_country: organization.country_iso_alpha2_code,
      organization_city: organization.city,
      success_url: getAbsoluteURL(resolveURLName("subscriptionSuccess", { organizationId: organization.id })),
      cancel_url: getCurrentURL(),
    });

    makeRequest({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      setValidAPIResponse: (data) => setCheckoutURL(data.checkout_session_url),
      credentials: "omit",
    });
  };

  React.useEffect(() => {
    if (checkoutURL) {
      setIsRedirecting(true);
      window.location.assign(checkoutURL);
    }
  }, [checkoutURL]);

  return { createCheckoutSession, isRedirecting, ...checkoutRequestState };
};

interface IOrganizationSubscriptionAPIResponse {
  subscription_id: string;
  trial_start: number;
  trial_end: number;
}

const useCreateSubscription = () => {
  const [subscriptionCreationResponse, setSubscriptionCreationResponse] =
    React.useState<IOrganizationSubscriptionAPIResponse>();

  const { makeRequest, makeRequestCleanUp, ...createSubscriptionRequestState } =
    useAPIRequest<IOrganizationSubscriptionAPIResponse>({
      apiBaseURL: subscriptionAPIBaseURL,
      apiServicePath: APISERVICEPATHS.subscriptionCreateOrganizationSubscription,
      makeRequestOnInitialMount: false,
    });

  const createSubscription = (args: {
    lookup_key: ILookupKey;
    organization: Organization;
    trial_period_days?: number;
  }) => {
    const { lookup_key, organization, trial_period_days } = args;

    if (!lookup_key || !organization) return;

    const body = JSON.stringify({
      lookup_key,
      organization_id: organization.id,
      organization_email: organization.email,
      organization_name: organization.name,
      organization_country: organization.country_iso_alpha2_code,
      organization_city: organization.city,
      trial_period_days: trial_period_days ?? 0,
    });

    makeRequest({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      setValidAPIResponse: (data) => setSubscriptionCreationResponse(data),
      credentials: "omit",
    });
  };

  return {
    createSubscription,
    subscriptionCreationResponse,
    ...createSubscriptionRequestState,
  };
};

const useCreatePortalSession = () => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const [portalSessionURL, setPortalSessionURL] = React.useState<string>();

  const { makeRequest, makeRequestCleanUp, ...apiRequestState } = useAPIRequest<any>({
    apiBaseURL: subscriptionAPIBaseURL,
    apiServicePath: APISERVICEPATHS.subscriptionCreatePortalSession,
    makeRequestOnInitialMount: false,
  });

  React.useEffect(() => {
    const body = JSON.stringify({
      organization_id: selectedOrganization.id,
      return_url: getAbsoluteURL(resolveURLName("dashboard")),
    });

    makeRequest({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      setValidAPIResponse: (data) => setPortalSessionURL(data.portal_session_url),
      credentials: "omit",
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization?.id]);

  return { portalSessionURL, ...apiRequestState };
};

const useGetOrganizationSubscription = (organizationId: Organization["id"]) => {
  const [subscriptionData, setSubscriptionData] = React.useState<IOrganizationSubscriptionAPIResponse>();

  const { makeRequest, makeRequestCleanUp, ...apiRequestState } = useAPIRequest<IOrganizationSubscriptionAPIResponse>({
    apiBaseURL: subscriptionAPIBaseURL,
    apiServicePath: APISERVICEPATHS.getOrganizationSubscription,
    makeRequestOnInitialMount: false,
  });

  React.useEffect(() => {
    makeRequest({
      apiPathParams: [organizationId],
      setValidAPIResponse: (data) => setSubscriptionData(data),
      credentials: "omit",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { subscriptionData, ...apiRequestState };
};

const useSubscriptionTrackerAPI = () => {
  const { makeRequest, ...remaining } = useAPIRequest<SubscriptionTracker>({
    apiBaseURL: subscriptionAPIBaseURL,
    apiServicePath: APISERVICEPATHS.subscriptionTrackers,
    makeRequestOnInitialMount: false,
    filterByGlobalOrganization: false,
  });

  const fetchSubscriptionTracker = React.useCallback(
    (
      organizationId: Organization["id"],
      setValidAPIResponse: Parameters<typeof makeRequest>[0]["setValidAPIResponse"]
    ) => {
      makeRequest({
        apiPathParams: [organizationId],
        credentials: "omit",
        method: "GET",
        setValidAPIResponse: (response) =>
          response?.organization_id && response?.product_code && setValidAPIResponse(response),
      });
    },
    [makeRequest]
  );

  return { ...remaining, fetchSubscriptionTracker };
};

export const useSaaSSubscriptionAPI = () => ({
  useSubscriptionPlansList,
  useCreateSetupCheckoutSession,
  useCreateCheckoutSession,
  useCreateSubscription,
  useCreatePortalSession,
  useGetOrganizationSubscription,
  useSubscriptionTrackerAPI,
});
